// My team's name and colors, from the screens (SCENARIOS L2).
//
// A manager can change their team's name and colors at any time, not only on
// joining. The Edit team sheet saves through here: a real league to
// `PUT /api/leagues/:id/teams/:teamId`, a demo league to the simulator. Which
// one is decided by the league on screen, before anything is sent, the rule
// `leagueSource.tsx` is built on.
//
// AFTER A SAVE, EVERYTHING THAT SHOWS THE TEAM IS READ AGAIN, without a
// reload: the hub (the switcher and the league cards read it), and every read
// of this league — the lineup's header, the scoreboard, the standings, the
// pre-draft seats — through the same signal a roster move uses
// (`movesChanged`). A name is read by id on every view, so asking again is all
// it takes.

import { useMemo } from 'react';
import {
  TeamRef,
  type ChosenBadge,
  type TeamBannerId,
  type TeamColors,
  type UpdateTeamInput,
} from '@fsp/domain';
import { messageOf, send, type Reply } from './api';
import { movesChanged } from './moves';
import { useActiveLeague, useLeagues } from './LeaguesProvider';
import { useLeagueSource } from './leagueSource';
import { useSim } from './SimProvider';
import { teamRef } from '@fsp/sim';

/** Rename or recolor my own team in a real league. The server's refusal comes back as it was said. */
export function updateTeam(
  leagueId: string,
  teamId: string,
  body: UpdateTeamInput,
): Promise<Reply<TeamRef>> {
  return send(
    'PUT',
    `/leagues/${encodeURIComponent(leagueId)}/teams/${encodeURIComponent(teamId)}`,
    body,
    TeamRef,
  );
}

/** The team the Edit team sheet is about, and where it lives. */
export interface MyTeamHere {
  team: TeamRef;
  leagueId: string;
  leagueName: string;
  /** The sport, for "Match a pro team". A value, never compared to a literal. */
  sport: string;
  source: 'api' | 'sim';
}

/**
 * My team in the league on screen, or null — a league I have no seat in, a
 * league still loading, or no league at all.
 *
 * A REAL LEAGUE'S TEAM COMES FROM THE HUB CARD, which every league has from the
 * moment it is made, in setup as well as in season — so the sheet opens with
 * no extra request, and the answer is the one the switcher is drawing.
 */
export function useMyTeamHere(): MyTeamHere | null {
  const source = useLeagueSource();
  const card = useActiveLeague();
  const { state, activeLeagueId, version, now } = useSim();
  return useMemo(() => {
    if (source === 'api') {
      if (card === null || card.myTeam === null) return null;
      return {
        team: card.myTeam,
        leagueId: card.league.id,
        leagueName: card.league.name,
        sport: card.league.sport,
        source: 'api',
      };
    }
    if (source !== 'sim' || activeLeagueId === null) return null;
    const mine = state.teams.find((t) => t.isMine);
    if (!mine) return null;
    return {
      team: teamRef(state, mine, now),
      leagueId: state.leagueId,
      leagueName: state.leagueName,
      sport: state.sport,
      source: 'sim',
    };
    // `version` is the simulator's "something changed": its teams are mutated
    // in place, so the same `state` object holds a new name.
  }, [source, card, state, activeLeagueId, version, now]);
}

/**
 * Save a change to my team, wherever it lives, and have every screen read it
 * again. Resolves to null when done, or to the sentence to show in the sheet.
 */
export function useSaveTeam(): (
  here: MyTeamHere,
  change: {
    name?: string;
    colors?: TeamColors;
    badge?: ChosenBadge;
    banner?: TeamBannerId | null;
  },
) => Promise<string | null> {
  const leagues = useLeagues();
  const { editTeam } = useSim();
  return async (here, change) => {
    // The practice league's teams wear their initials: a badge is a real
    // league's (B-440), as a banner is, and the rest is the simulator's to keep.
    if (here.source === 'sim') {
      const { badge: _badge, banner: _banner, ...rest } = change;
      return editTeam(rest);
    }
    const reply = await updateTeam(here.leagueId, here.team.id, change);
    // ASKED AGAIN WHATEVER CAME BACK, like a move: a refusal can mean the
    // league moved under the sheet, and asking costs one request.
    leagues.reload();
    setTimeout(() => movesChanged(here.leagueId), 0);
    if (reply.status === 'ok') return null;
    return messageOf(reply, 'That did not save.', 'Could not reach the site. Nothing was saved.');
  };
}
